import {
  ChevronRightTwoTone,
  HomeTwoTone,
  PeopleTwoTone,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Breadcrumbs,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Link as MLink,
  Stack,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { guideTourTransportationInfo } from '../api';
import GuideCustomerTransportationListDialog from '../components/GuideCustomerTransportationListDialog';
import { TransportationTable } from '../components/TransportationTable';
import { TransportationTableSkeleton } from '../components/TransportationTableSkeleton';
import { AuthContext } from '../context/AuthContext';
import { ITourCustomerResult, ITourFlightResult } from '../utils/types';

export function GuidanceTourTransportationPage() {
  const { t } = useTranslation();
  const { salesIndividualId } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [startData, setStartData] = useState<ITourFlightResult[]>([]);
  const [returnData, setReturnData] = useState<ITourFlightResult[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tourCustomerResult, setTourCustomerResult] =
    useState<ITourCustomerResult[]>();
  const { token } = useContext(AuthContext);
  const tourId = searchParams.get('tourId');

  useEffect(() => {
    if (!salesIndividualId) return;
    fetchGuideTourTransportationInfo(salesIndividualId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesIndividualId]);

  const fetchGuideTourTransportationInfo = async (id: string) => {
    setLoading(true);
    try {
      const res = await guideTourTransportationInfo(id, token);

      if (res?.status === 200) {
        const responseData = await res.json();

        if (Array.isArray(responseData) && responseData.length > 0) {
          const tourFlightResult = responseData[0]?.tourFlightResult;
          const tourCustomerResult = responseData[0]?.tourCustomerResult;

          if (tourCustomerResult) {
            setTourCustomerResult(tourCustomerResult);
          } else {
            console.error('tourCustomerResult is undefined.');
          }

          if (tourFlightResult) {
            const returnTransportation = tourFlightResult.filter(
              (d: ITourFlightResult) => d.direction?.toUpperCase() === 'RETURN',
            );
            const departureTransportation = tourFlightResult.filter(
              (d: ITourFlightResult) =>
                d.direction?.toUpperCase() === 'DEPARTURE',
            );

            setStartData(departureTransportation);
            setReturnData(returnTransportation);
          } else {
            console.error('tourFlightResult is undefined.');
          }
        } else {
          console.error('Response data is not in expected format.');
        }
      }
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  function handleCloseDialog() {
    setDialogOpen(false);
  }

  function handleOpenDialog() {
    setDialogOpen(true);
  }

  if (!salesIndividualId) return null;
  return (
    <Box sx={{ maxWidth: 'lg' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('GuidanceTourTransportationPage.title')}
      </Typography>
      <Breadcrumbs sx={{ mb: 1 }}>
        <MLink
          component={Link}
          to="/"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeTwoTone />
        </MLink>
        <MLink component={Link} to="/account">
          {t('nav.account')}
        </MLink>
        <MLink to="/account/guidance" component={Link}>
          <Typography>{t('nav.guidance')}</Typography>
        </MLink>
        <MLink
          component={Link}
          to={`/account/guidance/${salesIndividualId}?tourId=${tourId}`}
        >
          {t('GuidanceTourTransportationPage.tour', { tourId })}
        </MLink>
        <Typography>
          {t('GuidanceTourTransportationPage.transportation')}
        </Typography>
      </Breadcrumbs>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('GuidanceTourTransportationPage.startTitle')}
      </Typography>
      {loading ? (
        <TransportationTableSkeleton />
      ) : (
        <Box>
          {startData.length > 0 ? (
            <TransportationTable
              rows={startData.map((dr) => ({
                company: dr.transportationCompanyName,
                companyLogo: dr.transportationFullPathCompanyLogo,
                flightNumber: dr.number,
                departureDate: dr.departureDate,
                arrivalDate: dr.arrivalDate,
                departure: dr.departure,
                arrival: dr.arrival,
                arrivalTime: dr.arrivalTime,
                departureTime: dr.departureTime,
                durationInMinutes: dr.durationInMinutes,
              }))}
            />
          ) : (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('GuidanceTourTransportationPage.noTransportation')}
            </Alert>
          )}
        </Box>
      )}
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        {t('GuidanceTourTransportationPage.returnTitle')}
      </Typography>
      {/* eslint-disable-next-line no-constant-condition */}
      {loading ? (
        <TransportationTableSkeleton />
      ) : (
        <Box>
          {returnData.length > 0 ? (
            <TransportationTable
              rows={returnData.map((rr) => ({
                company: rr.transportationCompanyName,
                companyLogo: rr.transportationFullPathCompanyLogo,
                flightNumber: rr.number,
                departureDate: rr.departureDate,
                arrivalDate: rr.arrivalDate,
                departure: rr.departure,
                arrival: rr.arrival,
                arrivalTime: rr.arrivalTime,
                departureTime: rr.departureTime,
                durationInMinutes: rr.durationInMinutes,
              }))}
            />
          ) : (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('GuidanceTourTransportationPage.noTransportation')}
            </Alert>
          )}
        </Box>
      )}
      <Divider sx={{ mt: 4 }} />
      <List sx={{ maxWidth: 'sm' }}>
        <ListSubheader>
          {t('GuidanceTourTransportationPage.operations')}
        </ListSubheader>
        <ListItemButton onClick={handleOpenDialog}>
          <ListItemIcon>
            <PeopleTwoTone />
          </ListItemIcon>
          <ListItemText
            primary={t('GuidanceTourTransportationPage.customers')}
            secondary={t('GuidanceTourTransportationPage.numberOfCustomers', {
              count: tourCustomerResult?.length,
            })}
          />
          <Stack direction="row">
            <Chip
              color="info"
              label={t('GuidanceTourTransportationPage.domesticCustomers', {
                count: tourCustomerResult?.filter(
                  (c) => c.domesticArrival || c.domesticReturn,
                ).length,
              })}
            />
            <ChevronRightTwoTone />
          </Stack>
        </ListItemButton>
      </List>
      {dialogOpen && tourCustomerResult && (
        <GuideCustomerTransportationListDialog
          tourCustomerResult={tourCustomerResult}
          open={dialogOpen}
          onClose={handleCloseDialog}
          guideSalesIndividualId={salesIndividualId}
        />
      )}
    </Box>
  );
}
